<template>
  <Card class="mt-4 px-2" pt:body:class="p-3!" pt:content:class="">
    <template #header>
      <div v-if="messages.length" class="flex flex-col gap-2">
        <Message severity="error" :closeable="false">
          <div class="flex flex-col gap-1">
            <p v-for="error in messages">{{ error }}</p>
          </div>
        </Message>
      </div>
    </template>
    <template #content>
      <form @submit="onSubmit">
        <div class="grid grid-cols-2 gap-2">
          <div class="col-span-2">
            <StackedLabelInput name="subdivisionName" label="Name" classes="required" />
            <div class="flex flex-row gap-2">
              <StackedLabelDropdown
                name="userGroup"
                label="User Group"
                :options="userGroups"
                optionsLabel="name"
                classes="required"
                :disabled="userGroups.length <= 1"
              />
              <StackedLabelDatePicker name="maturityDate" label="Maturity Date" :max-date="new Date(3000, 1)" />
            </div>
          </div>
          <div class="flex flex-col gap-2 md:col-span-1 col-span-2">
            <StackedLabelInputNumber name="creditLine" label="Available Balance" mode="currency" />
            <StackedLabelInputNumber name="paidLand" label="Paid Land" mode="currency" />
            <StackedLabelInputNumber name="netCost" label="Commitment" mode="currency" />
          </div>
          <div class="flex flex-col gap-2 md:col-span-1 col-span-2">
            <StackedLabelInputNumber name="specLimit" label="Spec Limit" />
            <StackedLabelInputNumber name="modelLimit" label="Model Limit" />
            <StackedLabelInputNumber name="preSoldLimit" label="Pre-Sold Limit" />
          </div>
        </div>
        <div class="mt-4 flex justify-end">
          <SubmitButton />
        </div>
      </form>
    </template>
  </Card>
</template>

<script setup lang="ts">
import { useForm } from "vee-validate";
import { toTypedSchema } from "@vee-validate/zod";

import { z } from "zod";
import { CodeSchema, type Code } from "@/types/Codes";
import { onBeforeMount, ref } from "vue";
import { getUserGroups, handleApiResponse } from "@/services/Utility";
import axios from "axios";
import { success } from "@/services/ToastService";
import type { ApiError } from "@/types/Response";
import { SubdivisionSchema, type Subdivision } from "@/types/Subdivision";

const messages = ref<string[]>([]);
const userGroups = ref<Code[]>([]);

const emit = defineEmits<{
  subdivisionAdded: [subdivision: Subdivision];
}>();

onBeforeMount(async (): Promise<void> => {
  await getUserGroups(true, true).then((codes: Code[]) => (userGroups.value = codes));
  if (userGroups.value.length == 1) setFieldValue("userGroup", userGroups.value[0]);
});

const { handleSubmit, values, setFieldValue } = useForm({
  validationSchema: toTypedSchema(
    z.object({
      subdivisionName: z.string().min(1, { message: "A subdivision name is required" }),
      userGroup: CodeSchema.transform((code) => code.num),
      maturityDate: z.coerce.date().nullable(),
      creditLine: z.number().default(0),
      paidLand: z.number().default(0),
      netCost: z.number().default(0),
      specLimit: z.number().default(0),
      modelLimit: z.number().default(0),
      preSoldLimit: z.number().default(0),
    })
  ),
  keepValuesOnUnmount: true,
  initialValues: { maturityDate: null },
});

const onSubmit = handleSubmit((values: any): void => {
  axios.post("/subdivision/add", values).then((response) =>
    handleApiResponse(
      response,
      (data: any) => {
        success("Success", "Subdivision added");
        let newSubdivision: Subdivision = SubdivisionSchema.parse(data);
        emit("subdivisionAdded", newSubdivision);
      },
      (errors: ApiError[]) => {
        // Clear existing errors
        while (messages.value.length) {
          messages.value.pop();
        }
        messages.value.push("Please fix the following errors:");

        for (let error of response.data.errors) {
          messages.value.push(error.message);
        }
      }
    )
  );
});
</script>
